import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import StockPdfDialog from '../components/StockPdfDialog';
import { getAppSocket } from '@/lib/wesSocket';

type Product = {
  id: string;
  name: string;
  currentStock: number;
  minimumStock: number;
  isService: boolean;
  location?: string;
};

const ProductStock: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProducts = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/products', { params: { page: 1, limit: 500 } });
      const list = res.data?.products || res.data || [];
      const normalized = list.map((p: any) => ({
        id: p.id || p._id || p.uuid,
        name: p.name,
        currentStock: typeof p.currentStock !== 'undefined' ? p.currentStock : (typeof p.stock !== 'undefined' ? p.stock : 0),
        minimumStock: typeof p.minimumStock !== 'undefined' ? p.minimumStock : (typeof p.minStock !== 'undefined' ? p.minStock : 0),
        isService: p.isService ?? (p.category === 'servicio'),
        location: p.location || p.ubicacion || p.storageLocation || ''
      }));
      setProducts(normalized);
    } catch (error: any) {
      console.error('Error fetching products stock', error);
      toast.error(error?.response?.data?.error || 'No se pudo cargar el stock');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const realtimeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/products') ||
          path.startsWith('/api/purchases') ||
          path.startsWith('/api/supplies') ||
          path.startsWith('/api/remitos') ||
          path.startsWith('/api/invoices')
        )
      ) {
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchProducts({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, []);

  const [pdfOpen, setPdfOpen] = useState(false);

  const renderTable = (title: string) => {
    const rows = products.filter((p) => !p.isService && p.currentStock > 0);
    return (
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{title}</h3>
            <Badge variant="secondary">Total: {rows.length}</Badge>
          </div>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead>Ubicacion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => {
                  const low = p.minimumStock > 0 && p.currentStock <= p.minimumStock;
                  const out = p.currentStock <= 0 && p.minimumStock > 0;
                  return (
                    <TableRow key={p.id} className="border-b hover:bg-muted/50">
                      <TableCell>{p.name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <span>{p.currentStock}</span>
                          {low && (
                            <Badge variant="secondary" className={out ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}>
                              {out ? 'Sin stock' : 'Bajo'}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{p.location || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-7xl px-4 py-8 space-y-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Stock de Productos</h2>
            <p className="text-sm text-muted-foreground">Vision rapida del stock actual y minimos configurados.</p>
          </div>
          <Button onClick={() => setPdfOpen(true)} className="h-9 rounded-lg px-4" variant="outline">Generar PDF</Button>
        </div>

        <StockPdfDialog open={pdfOpen} onOpenChange={setPdfOpen} />

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-4">{renderTable('Productos')}</div>
        )}
      </div>
    </div>
  );
};

export default ProductStock;
